<template>
  <v-flex xs12 mt-4 v-if="isAltindafirsat">
    <v-card class="elevation-4">
      <v-card-title class="amber darken-3 white--text">
        <v-icon left color="white">handshake</v-icon>
        <span class="font-weight-bold">📅 Para Alış Takvimi — Önümüzdeki 7 Gün</span>
        <v-chip
          v-if="computeByCompany"
          small
          color="indigo"
          text-color="white"
          class="ml-2"
        >
          🏢 Şirket bazlı
        </v-chip>
        <v-spacer />
        <v-btn small color="white" class="amber--text text--darken-3 ma-0" @click.stop.prevent="goToFullPlan">
          Tüm Plan
          <v-icon right small>arrow_forward</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-3">
        <v-alert
          v-if="overdueCount > 0"
          :value="true"
          color="red darken-2"
          icon="warning"
          class="mb-3"
          outline
        >
          <strong>{{ overdueCount }} adet vadesi geçmiş anlaşma var.</strong>
          <router-link :to="'/mint-agreements'" class="ml-2">Detay →</router-link>
        </v-alert>

        <v-layout row wrap class="mb-3">
          <v-flex xs12 sm4 class="px-2 mb-2">
            <v-card outlined class="pa-2">
              <div class="caption grey--text text--darken-1">Toplam Beklenen Net Nakit</div>
              <div class="subtitle-1 font-weight-bold blue--text">
                {{ formatCurrency(grandNetCash) }}
              </div>
            </v-card>
          </v-flex>
          <v-flex xs12 sm4 class="px-2 mb-2">
            <v-card outlined class="pa-2">
              <div class="caption grey--text text--darken-1">
                Anlaşılmamış Sipariş
                <span v-if="carriedOverCount > 0" class="orange--text text--darken-2 ml-1">
                  (🔄 {{ carriedOverCount }} devreden)
                </span>
              </div>
              <div class="subtitle-1 font-weight-bold orange--text">
                {{ unallocatedCount }} adet
              </div>
            </v-card>
          </v-flex>
          <v-flex xs12 sm4 class="px-2 mb-2">
            <v-card outlined class="pa-2">
              <div class="caption grey--text text--darken-1">Bekleyen Anlaşma</div>
              <div class="subtitle-1 font-weight-bold">
                {{ pendingAgreementCount }} adet
              </div>
            </v-card>
          </v-flex>
        </v-layout>

        <v-data-table
          :headers="headers"
          :items="days"
          :loading="loading"
          hide-actions
          class="elevation-1"
        >
          <template v-slot:items="props">
            <tr :class="rowClass(props.item)">
              <td class="text-no-wrap">
                <strong>{{ formatDateWithDay(props.item.paymentDate) }}</strong>
                <span v-if="isToday(props.item.paymentDate)" class="ml-1 caption red--text">(Bugün)</span>
              </td>
              <td class="text-xs-right">
                {{ props.item.orderCount }}
                <small class="grey--text">
                  ({{ props.item.allocatedOrderCount }}A / {{ props.item.unallocatedOrderCount }}B)
                </small>
                <v-chip
                  v-if="props.item.carriedOverOrderCount > 0"
                  small
                  color="orange"
                  text-color="white"
                  class="ml-1"
                >
                  🔄 {{ props.item.carriedOverOrderCount }}
                </v-chip>
              </td>
              <td class="text-xs-right blue--text font-weight-bold">
                {{ formatCurrency(props.item.totalNetCash) }}
              </td>
              <td>
                <template v-if="props.item.existingAgreements && props.item.existingAgreements.length > 0">
                  <v-chip
                    v-for="ag in props.item.existingAgreements"
                    :key="ag.id"
                    small
                    :color="agreementChipColor(ag.status)"
                    text-color="white"
                    style="cursor: pointer"
                    @click="openDetail(ag)"
                  >
                    ✓ {{ ag.statusName }}
                  </v-chip>
                  <v-chip
                    v-if="props.item.unallocatedOrderCount > 0"
                    small
                    color="orange darken-2"
                    text-color="white"
                    class="font-weight-bold"
                  >
                    <v-icon left small>warning</v-icon>
                    {{ props.item.unallocatedOrderCount }} sipariş anlaşma bekliyor!
                  </v-chip>
                </template>
                <v-chip
                  v-else-if="props.item.unallocatedOrderCount > 0"
                  small
                  color="orange darken-2"
                  text-color="white"
                  class="font-weight-bold"
                >
                  <v-icon left small>warning</v-icon>
                  Anlaşma yok · {{ props.item.unallocatedOrderCount }} sipariş bekliyor
                </v-chip>
                <span v-else class="grey--text caption">—</span>
              </td>
            </tr>
          </template>
          <template v-slot:no-data>
            <v-alert :value="true" color="info" icon="info">
              Önümüzdeki 7 günde planlı sipariş yok.
            </v-alert>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <mint-agreement-detail-dialog
      v-model="detailDialog"
      :agreement-id="detailAgreementId"
    />
  </v-flex>
</template>

<script>
import MintAgreementDetailDialog from '@/components/MintAgreementDetailDialog.vue';

export default {
  name: 'MintAgreementSummaryCard',
  components: { MintAgreementDetailDialog },
  data() {
    return {
      loading: false,
      detailDialog: false,
      detailAgreementId: null,
      headers: [
        { text: 'Tarih', value: 'paymentDate', sortable: false },
        { text: 'Sipariş #', value: 'orderCount', sortable: false, align: 'right' },
        { text: 'Net Cash (₺)', value: 'totalNetCash', sortable: false, align: 'right' },
        { text: 'Anlaşma Durumu', value: 'agreementStatus', sortable: false },
      ],
    };
  },
  computed: {
    isAltindafirsat() {
      const t = parseInt(localStorage.getItem('selectedTenantId') || '0', 10);
      return t === 1;
    },
    plan() {
      return this.$store.getters['mintAgreements/plan'];
    },
    days() {
      return (this.plan && this.plan.days) || [];
    },
    overdueCount() {
      return (this.plan && this.plan.overdueAgreements && this.plan.overdueAgreements.length) || 0;
    },
    grandNetCash() {
      return (this.plan && this.plan.grandTotalNetCash) || 0;
    },
    unallocatedCount() {
      return (this.plan && this.plan.unallocatedOrderCount) || 0;
    },
    carriedOverCount() {
      return (this.plan && this.plan.totalCarriedOverOrderCount) || 0;
    },
    pendingAgreementCount() {
      return this.days.reduce((sum, d) => sum + ((d.existingAgreements || []).filter(a => a.status === 0).length), 0);
    },
    computeByCompany() {
      return !!(this.plan && this.plan.computeByCompany);
    },
  },
  mounted() {
    if (this.isAltindafirsat) this.loadPlan();
  },
  methods: {
    goToFullPlan() {
      if (this.$route.path !== '/mint-agreements') {
        this.$router.push('/mint-agreements').catch(() => {});
      }
    },
    async loadPlan() {
      this.loading = true;
      try {
        const today = new Date();
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const plus7 = new Date(today);
        plus7.setDate(plus7.getDate() + 7);
        await this.$store.dispatch('mintAgreements/getUpcomingPlan', {
          from: this.toIsoDate(tomorrow),
          to: this.toIsoDate(plus7),
        });
      } catch (e) {
        this.loading = false;
      } finally {
        this.loading = false;
      }
    },
    toIsoDate(d) {
      const dt = new Date(d);
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    },
    formatDateWithDay(v) {
      if (!v) return '—';
      const d = new Date(v);
      const dayName = d.toLocaleDateString('tr-TR', { weekday: 'short' });
      return `${d.toLocaleDateString('tr-TR')} ${dayName}`;
    },
    formatCurrency(v) {
      const n = Number(v) || 0;
      return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 2 }).format(n);
    },
    isToday(v) {
      if (!v) return false;
      const d = new Date(v);
      const t = new Date();
      return d.toDateString() === t.toDateString();
    },
    rowClass(day) {
      if (!day) return '';
      const today = this.isToday(day.paymentDate);
      const hasPending = (day.unallocatedOrderCount || 0) > 0;
      if (hasPending) return today ? 'orange lighten-4' : 'orange lighten-5';
      if (today) return 'amber lighten-5';
      return '';
    },
    openDetail(agreement) {
      this.detailAgreementId = agreement.id;
      this.detailDialog = true;
    },
    agreementChipColor(status) {
      if (status === 0) return 'orange';
      if (status === 1) return 'green';
      return 'grey';
    },
  },
};
</script>
